import React from 'react';
import Select, { StylesConfig, Props as ReactSelectProps, FormatOptionLabelMeta } from 'react-select';
import { useFormContext } from 'react-hook-form';

export interface SelectOption {
  value: string | number;
  label: string;
  isDisabled?: boolean;
  subLabel?: string;
  icon?: React.ReactNode;
}

// Available size options
export type SelectSize = 'sm' | 'md' | 'lg';

export interface CustomSelectProps {
  name: string;
  options: SelectOption[];
  label?: string;
  placeholder?: string;
  isRequired?: boolean;
  isSearchable?: boolean;
  isMulti?: ReactSelectProps['isMulti'];
  isClearable?: boolean;
  isDisabled?: boolean;
  defaultValue?: SelectOption | SelectOption[];
  helperText?: string;
  errorMessage?: string;
  customStyles?: StylesConfig;
  icon?: React.ReactNode;
  noOptionsMessage?: string;
  onChange?: (option: any) => void;
  className?: string;
  selectProps?: Partial<ReactSelectProps>;
  size?: SelectSize; // Size control option
}

interface ValueContainerProps {
  children: React.ReactNode;
  icon?: React.ReactNode; // explicitly define icon here
}

const ValueContainer: React.FC<ValueContainerProps> = ({ children, icon, ...props }) => {
  return (
    <div className="flex items-center pl-0 py-0 h-full relative" {...props}>
      {icon && <div className="mr-2 text-gray-400 flex-shrink-0">{icon}</div>}
      <div className="flex-grow relative h-full">{children}</div>
    </div>
  );
};

const IconValueContainer = (icon: React.ReactNode) => {
  return (props: ValueContainerProps) => <ValueContainer {...props} icon={icon} />;
};

// Custom components for the Select
const DropdownIndicator = (props: any) => {
  return (
    <div className="px-0 flex items-center justify-center text-gray-400 h-full">
      <svg width="14" height="9" viewBox="0 0 10 6" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M1 1L5 5L9 1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </div>
  );
};



const CustomSelect: React.FC<CustomSelectProps> = ({
  name,
  options,
  label,
  placeholder = 'Select...',
  isRequired = false,
  isSearchable = true,
  isMulti = false,
  isClearable = false,
  isDisabled = false,
  defaultValue,
  helperText,
  errorMessage,
  customStyles = {},
  icon,
  noOptionsMessage = 'No options available',
  onChange,
  className = '',
  selectProps = {},
  size = 'md', // Default to medium size
}) => {
  const { register, setValue, formState: { errors }, watch } = useFormContext() || {};
  const fieldError = errors?.[name]?.message;
  const errorMsg = errorMessage || (fieldError as string);

  // Register the field with react-hook-form
  React.useEffect(() => {
    if (register) {
      register(name);
    }
  }, [register, name]);

  // Set default value if provided
  React.useEffect(() => {
    if (setValue && defaultValue) {
      const value = isMulti
        ? (defaultValue as SelectOption[]).map(option => option.value)
        : (defaultValue as SelectOption).value;

      // Only set value if it's not already set
      if (value !== undefined && value !== null && value !== watch?.(name)) {
        setValue(name, value, { shouldDirty: false });
      }
    }
  }, [setValue, name, defaultValue, isMulti, watch]);





  // Get size-specific dimensions and styling
  const getSizeStyles = () => {
    switch (size) {
      case 'sm':
        return {
          height: '32px',
          minHeight: '32px',
          fontSize: '12px',
          paddingY: '0',
          valueContainerHeight: '30px',
        };
      case 'lg':
        return {
          height: '44px',
          minHeight: '44px',
          fontSize: '16px',
          paddingY: '4px',
          valueContainerHeight: '42px',
        };
      case 'md':
      default:
        return {
          height: '38px',
          minHeight: '38px',
          fontSize: '14px',
          paddingY: '0',
          valueContainerHeight: '36px',
        };
    }
  };

  const sizeStyles = getSizeStyles();

  // Default styles that can be overridden
  const defaultStyles: StylesConfig = {
    control: (base, state) => {
      //  default border color
      let borderColor = '#d1d5db';

      // error state → red
      if (errorMsg) {
        borderColor = '#f87171';
      }
      //  focused state → blue
      else if (state.isFocused) {
        borderColor = '#3b82f6';
      }

      return {
        ...base,
        height: sizeStyles.height,
        minHeight: sizeStyles.minHeight,
        borderRadius: '0.5rem',
        // extracted ternary into variable
        borderColor,
        boxShadow: state.isFocused ? '0 0 0 1px #3b82f6' : 'none',
        fontSize: sizeStyles.fontSize,
        '&:hover': {
          borderColor,
        },
        display: 'flex',
        alignItems: 'center',
        padding: `${sizeStyles.paddingY} 8px`,
      };
    },

    valueContainer: (base) => ({
      ...base,
      padding: '0',
      margin: '0',
      display: 'flex',
      alignItems: 'center',
      position: 'relative',
      top: 0,
      transform: 'none',
      flexWrap: 'nowrap',
      overflow: 'hidden',
      height: sizeStyles.valueContainerHeight,
    }),
    menu: (base) => ({
      ...base,
      zIndex: 9999,
      borderRadius: '0.5rem',
      overflow: 'visible',
      boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    }),
    menuList: (base) => ({
      ...base,
      padding: '4px',
      overflow: 'auto',
    }),
    option: (base, state) => {
      // default background
      let backgroundColor = 'white';

      // selected → blue
      if (state.isSelected) {
        backgroundColor = '#3b82f6';
      }
      // focused → light gray
      else if (state.isFocused) {
        backgroundColor = '#e5e7eb';
      }
      let optionPadding = '8px 12px';
      if (size === 'sm') optionPadding = '6px 10px';
      else if (size === 'lg') optionPadding = '10px 14px';
      return {
        ...base,
        backgroundColor,
        color: state.isSelected ? 'white' : 'inherit',
        cursor: 'pointer',
        borderRadius: '0.375rem',
        padding: optionPadding,
        fontSize: sizeStyles.fontSize,
        height: 'auto',
        overflow: 'visible',
        whiteSpace: 'normal',
        wordBreak: 'break-word',
        '&:active': {
          backgroundColor: '#dbeafe',
        },
      };
    },

    placeholder: (base) => ({
      ...base,
      color: '#9ca3af',
      marginLeft: '0',
      paddingLeft: '10px',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      position: 'absolute',
      top: '50%',
      transform: 'translateY(-50%)',
      paddingTop: '0',
      paddingBottom: '0',
      fontSize: sizeStyles.fontSize,
    }),
    singleValue: (base) => ({
      ...base,
      display: 'flex',
      alignItems: 'center',
      padding: '0',
      color: '#111827',
      position: 'absolute',
      top: '50%',
      transform: 'translateY(-50%)',
      margin: '0',
      fontSize: sizeStyles.fontSize,
    }),
    multiValue: (base) => {
      // default margin
      let margin = 3;

      if (size === 'sm') {
        margin = 2;
      } else if (size === 'lg') {
        margin = 4;
      }

      return {
        ...base,
        backgroundColor: '#e5e7eb',
        borderRadius: '0.25rem',
        // extracted ternary 
        margin,
      };
    },

    multiValueLabel: (base) => {
      // default padding
      let padding = '2px 6px';
      if (size === 'sm') padding = '1px 4px';
      else if (size === 'lg') padding = '3px 8px';

      // default fontSize
      let fontSize = '12px';
      if (size === 'sm') fontSize = '10px';
      else if (size === 'lg') fontSize = '14px';

      return {
        ...base,
        color: '#111827',
        padding,   // extracted padding
        fontSize,  // extracted fontSize
      };
    },


    multiValueRemove: (base) => ({
      ...base,
      color: '#6b7280',
      ':hover': {
        backgroundColor: '#ef4444',
        color: 'white',
      },
    }),
    indicatorSeparator: () => ({
      display: 'none',
    }),
    dropdownIndicator: (base) => ({
      ...base,
      padding: '0',
      alignItems: 'center',
      display: 'flex',
      justifyContent: 'center',
    }),
    clearIndicator: (base) => ({
      ...base,
      padding: '0 6px',
      color: '#9ca3af',
      ':hover': {
        color: '#6b7280',
      },
    }),
    indicatorsContainer: (base) => ({
      ...base,
      height: '100%',
      alignItems: 'center',
    }),
  };

  // Merge default styles with custom styles
  const finalStyles = { ...defaultStyles, ...customStyles };

  // Helper function to find the matching option(s) for the current value
  // Single-select handler
  type SelectValue = SelectOption | SelectOption[] | undefined;
  const findSingleValueOption = (
    options: SelectOption[],
    currentValue: any,
    defaultVal: SelectValue
  ) => {
    if (currentValue !== undefined && currentValue !== null && currentValue !== '') {
      return options.find(option => String(option.value) === String(currentValue)) || null;
    }
    return !Array.isArray(defaultVal) ? defaultVal || null : null;
  };

  // Multi-select handler
  const findMultiValueOption = (
    options: SelectOption[],
    currentValue: any,
    defaultVal: SelectValue
  ) => {
    if (Array.isArray(currentValue) && currentValue.length > 0) {
      return options.filter(option => currentValue.map(String).includes(String(option.value)));
    }
    return Array.isArray(defaultVal) ? defaultVal : [];
  };

  // Delegating function without inline ternary
  const findValueOption = (
    options: SelectOption[],
    currentValue: any,
    defaultVal: SelectValue,
    isMultiSelect: boolean
  ) => {
    const valueFinder = isMultiSelect ? findMultiValueOption : findSingleValueOption;
    return valueFinder(options, currentValue, defaultVal);
  };




  const handleChange = (selectedOption: any) => {
    if (!setValue) return;

    if (isMulti) {
      const values = selectedOption ? selectedOption.map((option: SelectOption) => option.value) : [];
      setValue(name, values, { shouldValidate: true });
    } else {
      setValue(name, selectedOption ? selectedOption.value : null, { shouldValidate: true });
    }

    if (onChange) {
      onChange(selectedOption);
    }
  };

  // Format option groups with sub-label if provided
  const formatOptionLabel = (data: unknown, formatOptionLabelMeta: FormatOptionLabelMeta<unknown>) => {
    const option = data as SelectOption;
    return (
      <div className="flex items-center">
        {option.icon && <div className="mr-2">{option.icon}</div>}
        <div>
          <div>{option.label}</div>
          {option.subLabel && (
            <div className="text-xs text-gray-500">{option.subLabel}</div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className={`mb-3 ${className}`}>
      {label && (
        <label className="block mb-1 text-sm font-medium text-gray-900 dark:text-gray-200">
          {label} {isRequired && <span className="text-red-500">*</span>}
        </label>
      )}
      <div className="relative">
        <Select
          {...selectProps}
          options={options}
          value={findValueOption(options, watch?.(name), defaultValue, isMulti)}
          onChange={handleChange}
          placeholder={placeholder}
          isSearchable={isSearchable}
          isMulti={isMulti as any}
          isClearable={isClearable}
          isDisabled={isDisabled}
          styles={finalStyles}
          className="rounded-lg"
          classNamePrefix="custom-select"
          components={{
            DropdownIndicator,
            ...(icon
              ? {
                ValueContainer: IconValueContainer(icon), // pass icon via the pre-defined wrapper
              }
              : {}),
          }}
          formatOptionLabel={formatOptionLabel}
          noOptionsMessage={() => noOptionsMessage}
        />
        {errorMsg && (
          <span className="text-red-500 text-xs mt-1 block">{errorMsg}</span>
        )}
        {helperText && !errorMsg && (
          <span className="text-gray-500 text-xs mt-1 block">{helperText}</span>
        )}
      </div>
    </div>
  );
};

export default CustomSelect;
